import React from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { Button, ButtonGroup, Grid, Typography } from '@mui/material';

import { useLazyQuery } from '@apollo/client';
import i18next from 'i18next';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ImportExportIcon from '@mui/icons-material/ImportExport';

import PagedTable from 'components/table/PagedTable';
import { filterSelector } from 'helper/filter';
import { userSelector, hasAnyAdminSpace } from 'helper/security';

import { PUBLIC_CLIENT_LIST_QUERY } from './gql';

import { ErrorAlert, RedirectError } from 'pages/error';

/**
 * Clients list page showing active and inactive clients separately.
 * Uses separate queries for active and inactive clients with pagination.
 */
export default function ClientsList() {
  const navigate = useNavigate();
  const filter = filterSelector();
  const user = userSelector()!;

  const [activeQuery, { data: activeData, loading: activeLoading, error: activeError }] = useLazyQuery(PUBLIC_CLIENT_LIST_QUERY);
  const [inactiveQuery, { data: inactiveData, loading: inactiveLoading, error: inactiveError }] = useLazyQuery(PUBLIC_CLIENT_LIST_QUERY);
  
  if (activeError) return <RedirectError err={activeError} />;
  if (inactiveError) return <RedirectError err={inactiveError} />;

  return (
    <>
      <Helmet>
        <title>{i18next.t('clients-list-page-title')}</title>
      </Helmet>
      <Grid container spacing={3}>
        {/* Active Clients */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            {i18next.t('publicclient-active-contacts')}
          </Typography>
          <PagedTable
            filterKey={`active-clients-${JSON.stringify(filter)}`}
            refetchPage={(skip, take) =>
              activeQuery({
                variables: {
                  where: { isActive: true },
                  skip,
                  take,
                },
              })
            }
            rowsLoading={activeLoading}
            headers={[
              i18next.t('clients-list-header-name'),
              i18next.t('clients-list-header-email'),
              i18next.t('clients-list-header-phone'),
              i18next.t('clients-list-header-company'),
              i18next.t('clients-list-header-premium'),
              i18next.t('clients-list-header-master'),
            ]}
            totalCount={activeData?.listPublicClients?.totalCount}
            rows={(activeData?.listPublicClients?.clients || []).map((row) => [
              <Link to={`/clients/clients/${row.id}`}>{row.firstname + ' ' + row.lastname}</Link>,
              row.email,
              row.phone || '-',
              <Link to={`/clients/companies/${row.publicCompany?.id}`}>{row.publicCompany?.name}</Link>,
              row.publicCompany?.isPremium ? <CheckIcon /> : '-',
              row.isMaster ? <CheckIcon /> : '-',
            ])}
          />
        </Grid>

        {/* Inactive Clients */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            {i18next.t('publicclient-inactive-contacts')}
          </Typography>
          <PagedTable
            filterKey={`inactive-clients-${JSON.stringify(filter)}`}
            refetchPage={(skip, take) =>
              inactiveQuery({
                variables: {
                  where: { isActive: false },
                  skip,
                  take,
                },
              })
            }
            rowsLoading={inactiveLoading}
            headers={[
              i18next.t('clients-list-header-name'),
              i18next.t('clients-list-header-email'),
              i18next.t('clients-list-header-phone'),
              i18next.t('clients-list-header-company'),
              i18next.t('clients-list-header-premium'),
              i18next.t('clients-list-header-master'),
            ]}
            totalCount={inactiveData?.listPublicClients?.totalCount}
            rows={(inactiveData?.listPublicClients?.clients || []).map((row) => [
              <Link to={`/clients/clients/${row.id}`}>{row.firstname + ' ' + row.lastname}</Link>,
              row.email,
              row.phone || '-',
              <Link to={`/clients/companies/${row.publicCompany?.id}`}>{row.publicCompany?.name}</Link>,
              row.publicCompany?.isPremium ? <CheckIcon /> : '-',
              row.isMaster ? <CheckIcon /> : '-',
            ])}
          />
        </Grid>
      </Grid>
    </>
  );
}
